import React from 'react';
import { SyncIcon } from '@fluentui/react-icons-mdl2';

import '../../GroupMgmt.css';
import { chatThemeCssVars } from '../../../theme';
import { useIdigestChatTheme } from '../../../ThemeContext';

export interface IConfirmDialogProps {
  show: boolean;
  title?: string;
  content: string | React.ReactNode;
  dialogClassName?: string;
  contentClassName?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  confirmText?: string;
  cancelText?: string;
  closeText?: string;
  loadingSubtext?: string;
  isLoading?: boolean;
  showCancel?: boolean;
  showConfirm?: boolean;
  closeOnOverlayClick?: boolean;
}

/**
 * 统一的确认对话框组件
 * 用于替代代码中重复的确认对话框实现
 *
 * @example
 * // 确认对话框（有确认和取消按钮）
 * <ConfirmDialog
 *   show={showDialog}
 *   title="确认删除"
 *   content="确定要删除这条消息吗？"
 *   onConfirm={handleConfirm}
 *   onCancel={() => setShowDialog(false)}
 * />
 *
 * @example
 * // 警告对话框（只有一个关闭按钮）
 * <ConfirmDialog
 *   show={showAlert}
 *   content="操作失败"
 *   onClose={() => setShowAlert(false)}
 *   showConfirm={false}
 *   showCancel={false}
 * />
 *
 * @example
 * // 带加载状态的确认对话框
 * <ConfirmDialog
 *   show={showDialog}
 *   title="确认删除"
 *   content="确定要删除这些群组吗？"
 *   onConfirm={handleConfirm}
 *   onCancel={() => setShowDialog(false)}
 *   isLoading={isDeleting}
 * />
 */
const ConfirmDialog: React.FC<IConfirmDialogProps> = ({
  show,
  title,
  content,
  dialogClassName,
  contentClassName,
  onConfirm,
  onCancel,
  onClose,
  confirmText,
  cancelText,
  closeText = 'OK',
  loadingSubtext,
  isLoading = false,
  showCancel = true,
  showConfirm = true,
  closeOnOverlayClick = true
}) => {
  const { theme, tokens } = useIdigestChatTheme();

  if (!show) {
    return null;
  }

  const themeClass =
    theme === 'dark' ? 'idigest-chat-dialog idigest-chat--dark' : 'idigest-chat-dialog idigest-chat--light';

  const handleOverlayClick = () => {
    if (closeOnOverlayClick) {
      if (onClose) {
        onClose();
      } else if (onCancel) {
        onCancel();
      }
    }
  };

  const handleDialogClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // 如果只显示关闭按钮（警告对话框模式）
  const isAlertMode = !showConfirm && !showCancel;

  return (
    <div
      className={`delete-confirm-overlay ${themeClass}`}
      style={chatThemeCssVars(tokens)}
      onClick={handleOverlayClick}
    >
      <div
        className={`delete-confirm-dialog${dialogClassName ? ` ${dialogClassName}` : ''}`}
        onClick={handleDialogClick}
      >
        {title && <div className='delete-confirm-title'>{title}</div>}
        <div className={`delete-confirm-content${contentClassName ? ` ${contentClassName}` : ''}`}>{content}</div>
        <div className='delete-confirm-buttons-wrap'>
          <div className='delete-confirm-buttons'>
            {showCancel && onCancel && (
              <button
                type='button'
                className='delete-confirm-button delete-confirm-button--secondary'
                onClick={onCancel}
                disabled={isLoading}
              >
                {cancelText || 'Cancel'}
              </button>
            )}
            {showConfirm && onConfirm && (
              <button
                type='button'
                className='delete-confirm-button'
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  void Promise.resolve(onConfirm()).catch((err) => {
                    console.error('ConfirmDialog onConfirm', err);
                  });
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    {!!loadingSubtext && <span className='delete-confirm-loading-subtext'>{loadingSubtext}</span>}
                    <SyncIcon
                      style={{
                        animation: 'spin 1s linear infinite',
                        display: 'inline-block'
                      }}
                    />
                    {confirmText || 'Confirm'}
                  </span>
                ) : (
                  confirmText || 'Confirm'
                )}
              </button>
            )}
            {isAlertMode && onClose && (
              <button type='button' className='delete-confirm-button' onClick={onClose}>
                {closeText}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
